import './StakeholderSelector.scss';

import classNames from 'classnames';
import { SonarStakeholder } from './SonarStakeholder';
import { Space } from 'antd';
import { StakeholderType } from '@sonar-software/sonar-core';
import { useStakeholders } from '@hooks';

import React, { useMemo } from 'react';
import { SonarSelector, SonarSelectorProps } from './SonarSelector';

export interface StakeholderSelectorProps extends Omit<SonarSelectorProps<number | number[]>, 'options' | 'showArrow'> {
	avatar?: boolean;
	stakeholderType?: StakeholderType
	withLatest?: boolean;
}

export const StakeholderSelector: React.FC<StakeholderSelectorProps> = ({
	avatar,
	className,
	direction = 'vertical',
	placeholder,
	popupClassName,
	stakeholderType,
	value,
	withLatest,
	...props
}) => {
	const stakeholders = useStakeholders(withLatest);
	const isMultiple = Array.isArray(value);

	const placeholderNode = useMemo(
		() => <SonarStakeholder
			avatar={ avatar }
			placeholder={ placeholder }
			stakeholderId={ -1 } />,
		[ avatar, placeholder ]
	);

	const options = useMemo(() => {
		const isSelected = (stakeholderId: number) =>
			(isMultiple ? value?.includes(stakeholderId) : stakeholderId === value);

		return (stakeholderType ?
			stakeholders.filter((stakeholder) => stakeholder.stakeholderType === stakeholderType) :
			stakeholders
		)
			.filter((stakeholder) => !stakeholder.isLocked || isSelected(stakeholder.stakeholderId))
			.map((stakeholder) => ({
				className: classNames({ disabled: stakeholder.isLocked }),
				label: <SonarStakeholder
					avatar={ avatar }
					stakeholderId={ stakeholder?.stakeholderId ?? -1 } />,
				optionLabel:
	<SonarStakeholder
		avatar={ avatar }
		showTooltip
		stakeholderId={ stakeholder?.stakeholderId ?? -1 } />,
				searchValue: stakeholder.name,
				value: stakeholder.stakeholderId
			}));
	},
	[ avatar, isMultiple, stakeholderType, stakeholders, value ]);

	const readOnlyDisplay = useMemo(
		() =>
			<Space
				direction={ direction }
				size={ 4 }
				wrap>
				{
					isMultiple ?
						value?.map(
							(stakeholderId) =>
								<SonarStakeholder
									key={ stakeholderId }
									showTooltip
									stakeholderId={ stakeholderId } />
						) :
						<SonarStakeholder
							key={ value }
							showTooltip
							stakeholderId={ Number(value) } />
				}
			</Space>
		,
		[ direction, isMultiple, value ]
	);

	const hasValue = Boolean(isMultiple ? value?.length : value);

	return (
		<SonarSelector
			{ ...props }
			className={ classNames('stakeholder-selector', className) }
			direction={ direction }
			options={ options }
			placeholder={ placeholderNode }
			popupClassName={ classNames('stakeholder-space-conscious-owner-label', popupClassName) }
			readOnlyDisplay={ hasValue ? readOnlyDisplay : null }
			showArrow
			value={ value } />
	);
};
